<div class="cyber-page">

  <!-- ── Header ──────────────────────────────────────────────────────────── -->
  <div class="cyber-header">
    <div class="header-left">
      <h1 class="cyber-title">Cyber &amp; System Health</h1>
    </div>
  </div>

  <!-- ── Controls ────────────────────────────────────────────────────────── -->
  <div class="control-bar">
    <div class="summary-pills">
      <span class="pill critical"><span class="pill-dot"></span>{{ criticalCount() }} Critical</span>
      <span class="pill warning"><span class="pill-dot"></span>{{ warningCount() }} Warning</span>
      <span class="pill nominal"><span class="pill-dot"></span>{{ nominalCount() }} Nominal / Unknown</span>
      <span class="pill total">{{ survivingAlerts().length }} Total</span>
    </div>

    <div class="source-controls">
      @if (scenarios().length > 0) {
        <select
          class="topo-select"
          [value]="selectedScenario() ?? ''"
          [disabled]="replayRunning()"
          (change)="onScenarioChange($event)"
        >
          @for (scenario of scenarios(); track scenario.id) {
            <option [value]="scenario.id">{{ scenario.label }}</option>
          }
        </select>

        <button
          class="ctl-btn"
          type="button"
          [class.danger]="replayRunning()"
          [disabled]="!selectedScenario()"
          [title]="
            replayRunning()
              ? 'Stop publishing the recording'
              : 'Publish this recording to the cyber detection feed'
          "
          (click)="toggleReplay()"
        >
          {{ replayRunning() ? '⏹ Stop replay' : '▶ Replay' }}
        </button>

        @if (replayStatus(); as status) {
          @if (status.running) {
            <span class="replay-progress">
              {{ status.window }}/{{ status.total }} · {{ status.alertsSent }} sent
            </span>
          }
        }
      }

      <button class="ctl-btn" type="button" (click)="toggleBasemap()">🗺 Basemap</button>
    </div>
  </div>

  @if (replayError(); as message) {
    <div class="state-message state-error">{{ message }}</div>
  }

  <!-- ── Layer toggles ───────────────────────────────────────────────────── -->
  <div class="layer-bar">
    @for (layer of layers; track layer.id) {
      <button class="layer-btn" [class.active]="isLayerActive(layer.id)" (click)="toggleLayer(layer.id)">
        {{ layer.label }}
      </button>
    }
  </div>

  @if (loading()) {
    <div class="state-message">Loading…</div>
  }
  @if (error()) {
    <div class="state-message state-error">{{ error() }}</div>
  }

  <!-- ── Main grid ───────────────────────────────────────────────────────── -->
  @if (!loading()) {
    <div class="main-grid">

      <!-- Map — no header: the drawing names itself on hover -->
      <section class="panel panel-map">
        <div class="map-container">
          <div id="cyber-map" #mapEl></div>
        </div>
      </section>

      <!-- Recent alerts -->
      <section class="panel panel-alerts">
        <div class="panel-header">
          <span class="panel-title">Recent Alerts</span>
          <!--
            An analyst sees their own queue, so an empty page is a normal state rather than a
            broken one. Saying so is what stops it reading as a fault.
          -->
          @if (restrictedToAssigned) {
            <span class="scope-chip" title="This page shows only the alerts assigned to you">
              Assigned to you
            </span>
          }
          <!--
            Selection is marked on the options rather than with [value] on the select: the value
            binding is applied before @for has created any option to match, so it finds nothing
            and the browser falls back to showing the first one.
          -->
          <select class="topo-select range-select" (change)="onRecentRangeChange($event)">
            @for (range of recentRanges; track range.hours) {
              <option [value]="range.hours" [selected]="range.hours === recentRangeHours()">
                {{ range.label }}
              </option>
            }
          </select>
          <span class="panel-count">{{ recentAlerts().length }}</span>
        </div>
        <div class="alert-list vg-scroll">
          @if (recentAlerts().length === 0) {
            <div class="empty-list">
              @if (survivingAlerts().length > 0) {
                No alerts in the selected range.
              } @else if (restrictedToAssigned) {
                No alerts are assigned to you.
              } @else {
                No alerts to display.
              }
            </div>
          }
          @for (alert of recentAlerts(); track alert.id) {
            <div class="alert-row" (click)="openPopupFromList(alert)">
              <span class="severity-dot" [class]="getSeverity(alert)"></span>
              <div class="alert-row-content">
                <span class="alert-node">{{ alert.legalName }}</span>
                <span class="alert-desc">
                  {{ alert.anomaly?.anomalyType ?? (alert.intentions.length > 0 ? 'Intention alert' : 'Unclassified') }}
                  @if (alert.anomaly?.anomalyScore != null) {
                    · {{ alert.anomaly!.anomalyScore | number: '1.2-2' }}
                  }
                </span>
              </div>
              <span class="alert-time">{{ alert.generatedIn | date: 'HH:mm' }}</span>
              <span [class]="getBadgeClass(alert)">{{ badgeLabel(alert) }}</span>
            </div>
          }
        </div>
      </section>

      <!-- Network topology — full width and tall, so the whole schema reads at a glance -->
      <section class="panel panel-network">
        <div class="panel-header">
          <span class="panel-title">Network topology</span>

          @if (!editorDraft()) {
            @if (topologies().length > 0) {
              <select
                class="topo-select"
                [value]="topology()?.id ?? ''"
                (change)="onTopologyChange($event)"
              >
                @if (ownedTopologies().length > 0) {
                  <optgroup label="My topologies">
                    @for (option of ownedTopologies(); track option.id) {
                      <option [value]="option.id">{{ option.name }}</option>
                    }
                  </optgroup>
                }
                @if (sharedTopologies().length > 0) {
                  <optgroup label="Shared with me">
                    @for (option of sharedTopologies(); track option.id) {
                      <option [value]="option.id">
                        {{ option.name }} — {{ option.ownerUsername ?? 'shared' }}
                      </option>
                    }
                  </optgroup>
                }
              </select>
            }

            <div class="panel-actions">
              <button class="ctl-btn" type="button" (click)="newTopology()">+ New</button>
              <button
                class="ctl-btn"
                type="button"
                [disabled]="!activeTopologySummary()?.owned"
                title="Only the owner can edit a topology"
                (click)="editTopology()"
              >
                Edit
              </button>
              <button
                class="ctl-btn"
                type="button"
                [disabled]="!activeTopologySummary()?.owned"
                (click)="openShare()"
              >
                Share
              </button>
              <button
                class="ctl-btn danger"
                type="button"
                [disabled]="!activeTopologySummary()?.owned"
                (click)="deleteTopology()"
              >
                Delete
              </button>
            </div>
          }
        </div>

        @if (topologyError(); as message) {
          <div class="state-message state-error">{{ message }}</div>
        }

        <div class="network-body">
          @if (editorDraft(); as draft) {
            <app-topology-editor
              [draft]="draft"
              [saving]="topologySaving()"
              [error]="topologyError()"
              (saved)="saveTopology($event)"
              (cancelled)="cancelEditing()"
            />
          } @else if (topologies().length === 0) {
            <div class="empty-topology">
              <p>No network topology yet.</p>
              <p class="empty-hint">
                Draw the pipeline or cable you monitor, and alerts will light up the devices
                they name.
              </p>
              <button class="ctl-btn" type="button" (click)="newTopology()">
                Create a topology
              </button>
            </div>
          } @else {
            <app-network-topology
              [topology]="topology()"
              [state]="cyber()"
              [selected]="selectedNodes()"
            />
          }
        </div>
      </section>
    </div>
  }

  <!-- ── Alert chooser — several alerts share one marker ─────────────────── -->
  @if (alertChooser(); as group) {
    <div class="chooser-backdrop" (click)="closeChooser()">
      <div class="chooser-card" (click)="$event.stopPropagation()">
        <div class="chooser-header">
          <span class="chooser-title">{{ group.length }} alerts at this location</span>
          <button class="popup-close" type="button" (click)="closeChooser()">✕</button>
        </div>
        <div class="chooser-list vg-scroll">
          @for (alert of group; track alert.id) {
            <div class="alert-row" (click)="openPopup(alert)">
              <span class="severity-dot" [class]="getSeverity(alert)"></span>
              <div class="alert-row-content">
                <span class="alert-node">{{ alert.legalName }}</span>
                <span class="alert-desc">
                  {{ alert.anomaly?.anomalyType ?? (alert.intentions.length > 0 ? 'Intention alert' : 'Unclassified') }}
                  @if (alert.anomaly?.anomalyScore != null) {
                    · {{ alert.anomaly!.anomalyScore | number: '1.2-2' }}
                  }
                </span>
              </div>
              <span class="alert-time">{{ alert.generatedIn | date: 'HH:mm' }}</span>
              <span [class]="getBadgeClass(alert)">{{ badgeLabel(alert) }}</span>
            </div>
          }
        </div>
      </div>
    </div>
  }

  <!-- ── Alert dialog — page level and centred, so nothing can clip it ────── -->
  @if (selectedAlert(); as alert) {
    <div
      class="map-popup vg-scroll"
      #popupEl
      [class.centered]="popupCentered()"
      [style.left.px]="popupCentered() ? null : popupX()"
      [style.top.px]="popupCentered() ? null : popupY()"
    >
      <div
        class="popup-header"
        [class]="'popup-header-' + getSeverity(alert)"
        (mousedown)="onPopupHeaderMouseDown($event)"
      >
        <div class="popup-header-left">
          <span class="popup-node-name">{{ alert.legalName }}</span>
        </div>
        <button class="popup-close" (click)="closePopup()">✕</button>
      </div>

      <div class="popup-body">
        <div class="semaphore">
          <div class="light red" [class.lit]="isLit(getSeverity(alert), 'red')" title="Critical"></div>
          <div class="light amber" [class.lit]="isLit(getSeverity(alert), 'amber')" title="Warning"></div>
          <div class="light green" [class.lit]="isLit(getSeverity(alert), 'green')" title="Nominal"></div>
          <span class="semaphore-label">{{ getSeverity(alert) | titlecase }}</span>
        </div>

        <div class="meta-grid">
          <span class="meta-key">Service</span>
          <span class="meta-val">{{ alert.legalName }}</span>

          <span class="meta-key">Type</span>
          <span class="meta-val">{{ alertKindLabel(alert) }}</span>

          <span class="meta-key">Timestamp</span>
          <span class="meta-val">{{ alert.generatedIn | date: 'yyyy-MM-dd HH:mm' }}</span>

          <span class="meta-key">Status</span>
          <span class="meta-val">{{ alert.status }}</span>
        </div>

        @if (alert.anomaly?.anomalyScore != null || alert.intentions.length > 0) {
          <div class="confidence-section">
            <div class="confidence-label">
              <span>Confidence / Score</span>
              <span class="confidence-pct">{{ confidencePercent(alert) }}%</span>
            </div>
            <div class="confidence-bar">
              <div
                class="confidence-fill"
                [class]="'fill-' + getSeverity(alert)"
                [style.width.%]="confidencePercent(alert)"
              ></div>
            </div>
          </div>
        }

        @if (alert.anomaly?.explanation) {
          <p class="popup-explanation">{{ alert.anomaly!.explanation }}</p>
        }

        @if (alert.intentions.length > 0) {
          <div class="intentions-section">
            <div class="intentions-title">Threat Intentions</div>
            @for (intention of alert.intentions; track $index) {
              <div class="intention-item">
                <div class="intention-row">
                  <span class="intention-label">{{ intention.classification }}</span>
                  <div class="intention-bar-wrap">
                    <div class="intention-bar-fill" [style.width.%]="intention.probability * 100"></div>
                  </div>
                  <span class="intention-pct">{{ intention.probability * 100 | number: '1.0-0' }}%</span>
                </div>
                @if (intention.explanation) {
                  <p class="intention-explanation">{{ intention.explanation }}</p>
                }
              </div>
            }
          </div>
        }
      </div>

      <div class="popup-footer">
        <!-- The message exchange panel is gone, so the raw CISE XML lives here now. -->
        <button class="popup-btn btn-xml" (click)="openAlertXml(alert)">View XML</button>
        <!--
          Deciding about an alert — acknowledging it, working it — happens where the context for
          that decision is, so this dialog only offers the way there: Incident Management for those
          who work incidents, the monitoring page for everyone else, and then only for an alert
          that is theirs.
        -->
        @if (canManageIncidents) {
          <button
            class="popup-btn btn-report"
            [routerLink]="['/incidents']"
            [queryParams]="{ alertId: alert.id }"
          >
            Go to Alert
          </button>
        } @else if (isAssignedToMe(alert)) {
          <button
            class="popup-btn btn-report"
            [routerLink]="['/alerts']"
            [queryParams]="{ alertId: alert.id }"
          >
            Go to Alert
          </button>
        }
      </div>
    </div>
  }

  <!-- ── Alert pop-ups — stack, and stay until dismissed ─────────────────── -->
  @if (toasts().length > 0) {
    <div class="toast-stack">
      @for (t of toasts(); track t.id) {
        <div class="alert-toast" [class]="'tone-' + t.tone">
          <button class="toast-close" type="button" (click)="dismissToast(t.id)">✕</button>
          <div class="toast-title">{{ t.title }}</div>
          @if (t.text) {
            <div class="toast-text">{{ t.text }}</div>
          }
        </div>
      }
    </div>
  }

  <!-- ── Topology sharing ────────────────────────────────────────────────── -->
  @if (sharingTopology(); as summary) {
    <app-topology-share-dialog [topology]="summary" (closed)="closeShare()" />
  }

  <!-- ── Delete confirmation ─────────────────────────────────────────────── -->
  @if (pendingDelete(); as doomed) {
    <div class="chooser-backdrop" (click)="cancelDelete()">
      <div class="chooser-card" (click)="$event.stopPropagation()">
        <div class="chooser-header">
          <span class="chooser-title">Delete topology</span>
          <button class="popup-close" type="button" (click)="cancelDelete()">✕</button>
        </div>

        <div class="confirm-body">
          <p class="confirm-question">Delete “{{ doomed.name }}”?</p>
          <p class="confirm-hint">
            The diagram goes, and so does everyone's access to it. This cannot be undone.
          </p>
        </div>

        <div class="confirm-actions">
          <button class="ctl-btn" type="button" (click)="cancelDelete()">Cancel</button>
          <button class="ctl-btn danger" type="button" (click)="confirmDelete()">Delete</button>
        </div>
      </div>
    </div>
  }

  <!-- ── XML spotlight ───────────────────────────────────────────────────── -->
  @if (spotlight(); as spot) {
    <app-xml-spotlight
      [title]="spot.title"
      [subtitle]="spot.subtitle"
      [xml]="spotlightXml()"
      [loading]="spotlightLoading()"
      [error]="spotlightError()"
      (closed)="closeSpotlight()"
    />
  }
</div>
